import { useState, type FormEvent } from 'react';
import SectionHeading from '@/components/feature/SectionHeading';
import Reveal from '@/components/feature/Reveal';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/daio2croh653ivfvohc0';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactSection() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honey = String(formData.get('website_alt') ?? '').trim();
    if (honey) {
      setStatus('success');
      setFormError('');
      form.reset();
      return;
    }

    setStatus('submitting');
    setFormError('');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') {
        return;
      }
      const text = typeof value === 'string' ? value.trim() : '';
      if (text) {
        payload.append(key, text);
      }
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = typeof serverMsg === 'string' && /spam|form data is spam/i.test(serverMsg);

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        setFormError('');
        form.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg || '提交没有成功，请稍后再试一次。');
      }
    } catch {
      setStatus('error');
      setFormError('网络好像不太顺畅，请稍后再试一次。');
    }
  };

  return (
    <section className="w-full px-4 md:px-8 lg:px-12 py-14 md:py-20 bg-background-100">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
        <div>
          <SectionHeading
            eyebrow="Contact"
            title="把你的现场也告诉我们"
            description="如果你在公开活动中拍到了值得记录的瞬间，或者有清晰的现场观察，欢迎写给我们。请尽量附上时间和地点，方便我们核对。"
          />

          <div className="mt-8 space-y-4">
            <div className="flex items-start gap-3">
              <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-700 shrink-0">
                <i className="ri-mail-line"></i>
              </span>
              <div>
                <p className="text-sm font-medium text-foreground-900">投稿与留言邮箱</p>
                <p className="text-sm text-foreground-600">hello@511qunzhong.space</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-10 h-10 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-800 shrink-0">
                <i className="ri-time-line"></i>
              </span>
              <div>
                <p className="text-sm font-medium text-foreground-900">整理节奏</p>
                <p className="text-sm text-foreground-600">热榜话题当天或次日更新，现场记录一般活动后一天内整理</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-100 text-accent-800 shrink-0">
                <i className="ri-shield-check-line"></i>
              </span>
              <div>
                <p className="text-sm font-medium text-foreground-900">内容原则</p>
                <p className="text-sm text-foreground-600">只整理公开见闻，不做猜测，欢迎来信更正或申请下架</p>
              </div>
            </div>
          </div>
        </div>

        <Reveal>
          <div className="rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-7">
            <h3 className="font-heading font-bold text-lg text-foreground-950 mb-5">留下你的观察</h3>
            <form
              id="contact-form-511"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-4"
              noValidate
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="cf-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    称呼
                  </label>
                  <input
                    id="cf-name"
                    name="nickname"
                    type="text"
                    required
                    placeholder="怎么称呼你"
                    className="w-full px-3.5 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
                  />
                </div>
                <div>
                  <label htmlFor="cf-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    邮箱
                  </label>
                  <input
                    id="cf-email"
                    name="email"
                    type="email"
                    required
                    placeholder="name@example.com"
                    className="w-full px-3.5 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="cf-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  想说的内容
                </label>
                <textarea
                  id="cf-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="时间、地点、你看到的细节……越具体越好（最多 500 字）"
                  className="w-full px-3.5 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 resize-none"
                ></textarea>
              </div>

              <input
                className="form-aux-field"
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-primary-500 text-background-50 font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
              >
                <i className="ri-send-plane-line"></i>
                {status === 'submitting' ? '正在提交…' : '提交留言'}
              </button>

              {status === 'success' && (
                <p className="text-sm text-secondary-800 bg-secondary-100 rounded-md px-3.5 py-2.5">
                  已经收到啦，感谢分享。我们会尽快核对整理。
                </p>
              )}
              {status === 'error' && (
                <p className="text-sm text-primary-800 bg-primary-100 rounded-md px-3.5 py-2.5">{formError}</p>
              )}
            </form>
          </div>
        </Reveal>
      </div>
    </section>
  );
}